/**
 * Coerce an arbitrary thrown value into a human-readable string.
 *
 * Why this exists: Eden Treaty surfaces backend errors as
 * `{ status, value }` envelopes, and calling `String(...)` on those
 * produces the literal text `[object Object]` — which would otherwise
 * land in toasts as the user-visible message. `extractErrorMessage`
 * unwraps the envelope and pulls a sensible string regardless of shape.
 *
 * Handles, in order of preference:
 *  - `Error` instances           → `.message`
 *  - `string`                    → as-is
 *  - Eden Treaty `{ value }`     → `.value.error.message` (our `apiError` shape)
 *                                  → `.value.message` or `.value` if string
 *                                  → `HTTP {status}` as last resort
 *  - `{ error: { message } }`    → `.error.message` (raw fetch of the same envelope)
 *  - `{ message: string }`       → `.message`
 *  - Anything else               → JSON.stringify, or `"Unknown error"`
 */
export function extractErrorMessage(err: unknown): string {
  const message = coerceErrorMessage(err)
  // Final guardrail: an upstream layer may have already coerced an object via
  // `String(...)` (or thrown `new Error(someObject)`), so the message reaching us
  // can be the useless literal `[object Object]`. Never surface that to a user —
  // it's the one string this function must never return, whatever the path.
  return message && message !== "[object Object]" ? message : "Unknown error"
}

function coerceErrorMessage(err: unknown): string {
  if (err == null) return "Unknown error"
  if (typeof err === "string") return err.length > 0 ? err : "Unknown error"
  if (err instanceof Error) return err.message.length > 0 ? err.message : "Unknown error"

  if (typeof err === "object") {
    const e = err as Record<string, unknown>

    if ("value" in e) {
      const v = e.value
      if (typeof v === "string" && v.length > 0) return v
      if (v && typeof v === "object") {
        const vo = v as Record<string, unknown>
        if (vo.error && typeof vo.error === "object") {
          const inner = vo.error as Record<string, unknown>
          if (typeof inner.message === "string" && inner.message.length > 0) return inner.message
        }
        if (typeof vo.message === "string" && vo.message.length > 0) return vo.message
      }
      if ("status" in e && typeof e.status === "number") return `HTTP ${e.status}`
    }

    // Raw fetch (multipart uploads) sees the API envelope unwrapped by Eden:
    // `{ error: { code, message } }`. extractErrorCode already reads this shape.
    if (e.error && typeof e.error === "object") {
      const inner = e.error as Record<string, unknown>
      if (typeof inner.message === "string" && inner.message.length > 0) return inner.message
    }

    if (typeof e.message === "string" && e.message.length > 0) return e.message
    if ("status" in e && typeof e.status === "number") return `HTTP ${e.status}`
    // Transport-level failures (fetch/parse errors surfaced by Eden) often carry
    // only a `code` or `name` — surface that rather than collapsing to the
    // useless "Unknown error".
    if (typeof e.code === "string" && e.code.length > 0) return e.code
    if (typeof e.name === "string" && e.name.length > 0 && e.name !== "Object") return e.name

    try {
      const json = JSON.stringify(err)
      return json && json !== "{}" ? json : "Unknown error"
    } catch {
      return "Unknown error"
    }
  }

  const coerced = String(err)
  return coerced === "[object Object]" ? "Unknown error" : coerced
}

/**
 * Extract the machine-readable error `code` from an Eden Treaty error result,
 * if present. Mirrors {@link extractErrorMessage} but returns the stable code
 * string (`"NOT_FOUND"`, `"PAYMENT_REQUIRED"`, a module domain code, …) so a
 * call site can branch on the *kind* of failure without parsing prose. Returns
 * `undefined` when there's no code (transport errors, old bare shapes).
 */
export function extractErrorCode(err: unknown): string | undefined {
  if (err == null || typeof err !== "object") return undefined
  const e = err as Record<string, unknown>
  const value = "value" in e ? e.value : e
  if (value && typeof value === "object") {
    const vo = value as Record<string, unknown>
    if (vo.error && typeof vo.error === "object") {
      const inner = vo.error as Record<string, unknown>
      if (typeof inner.code === "string" && inner.code.length > 0) return inner.code
    }
  }
  return undefined
}

/**
 * Eden Treaty result envelope. Exposed so call sites that need to reshape a
 * dynamic-path response (where the auto-inferred type is too loose) can cast to
 * `IUnwrapped<T>` and then `unwrap` it, instead of each declaring their own copy.
 */
export interface IUnwrapped<T> {
  data: T | null
  error: unknown
}

/**
 * Eden Treaty result whose SUCCESS body is the canonical `{ data: T }` envelope
 * (the standard response shape — see `backend/src/lib/response.ts`). Cast a
 * dynamic-path Eden result to this and hand it to {@link unwrapData}.
 */
export interface IEnvelopeResult<T> {
  data: { data: T } | null
  error: unknown
}

/**
 * Unwrap an Eden Treaty `{ data, error }` result into its non-null payload,
 * throwing a human-readable `Error` on failure.
 *
 * Every typed API wrapper in `lib/api/*` previously declared its own copy of
 * this helper (six near-identical definitions, one of which used bare
 * `String(error)` instead of `extractErrorMessage` and so leaked
 * `[object Object]` into toasts). Consolidating to one definition keeps the
 * error-coercion behaviour identical across every call site.
 *
 * The `{ data: T }` signature covers both historical variants: callers that
 * pass `{ data: T | null }` simply infer `T` as `T | null`, and
 * `NonNullable<T | null>` collapses back to `T` — so the return type is always
 * the non-null payload regardless of how the result type was declared.
 */
export function unwrap<T>(result: { data: T; error: unknown }): NonNullable<T> {
  const { data, error } = result
  if (error) throw new Error(extractErrorMessage(error))
  if (data == null) throw new Error("API returned no data")
  return data
}

/**
 * Unwrap an Eden Treaty result whose success body is the canonical `{ data: T }`
 * envelope, returning the inner payload `T`. This is {@link unwrap} plus one peel
 * of the response envelope — the standard call for any endpoint migrated to the
 * `{ data }` shape (Phase 4). Legacy bare-body endpoints keep using `unwrap`;
 * the two coexist through the migration and each wrapper picks the one matching
 * its backend.
 *
 * For a PAGINATED endpoint (`{ data, meta }`) use `unwrap(result)` and read
 * `.data` / `.meta` directly — `unwrapData` is for the single-payload `{ data }`.
 */
export function unwrapData<T>(result: IEnvelopeResult<T>): NonNullable<T> {
  const { data, error } = result
  if (error) throw new Error(extractErrorMessage(error))
  if (data == null || data.data == null) throw new Error("API returned no data")
  return data.data as NonNullable<T>
}
